import { useEffect, useState } from 'react';

import type { FacilityDto } from '../../organization/types/facility';
import type { RoleId } from '../types/user';
import { roleCatalog } from '../types/user';

export function InviteUserDialog({
  facilities,
  allowOrganizationWide,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  facilities: FacilityDto[];
  allowOrganizationWide: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: { email: string; roleId: RoleId; facilityIds: string[] }) => void;
}) {
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<RoleId>('viewer');
  const [scopeMode, setScopeMode] = useState<'ALL' | 'SELECTED'>(allowOrganizationWide ? 'ALL' : 'SELECTED');
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([]);

  useEffect(() => {
    setEmail('');
    setRoleId('viewer');
    setScopeMode(allowOrganizationWide ? 'ALL' : 'SELECTED');
    setSelectedFacilityIds([]);
  }, [allowOrganizationWide]);

  const validEmail = /^\S+@\S+\.\S+$/.test(email.trim());
  const validScope = (scopeMode === 'ALL' && allowOrganizationWide) || (scopeMode === 'SELECTED' && selectedFacilityIds.length > 0);
  const valid = validEmail && validScope;

  function toggleFacility(facilityId: string) {
    setSelectedFacilityIds((current) => current.includes(facilityId)
      ? current.filter((item) => item !== facilityId)
      : [...current, facilityId]);
  }

  return (
    <div className="r42-dialog-layer" role="dialog" aria-modal="true" aria-labelledby="invite-user-title">
      <button className="r42-dialog-backdrop" aria-label="Close invite user" onClick={busy ? undefined : onClose} />
      <form
        className="r42-dialog-card r42-user-dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid && !busy) onSubmit({ email: email.trim(), roleId, facilityIds: scopeMode === 'ALL' ? [] : selectedFacilityIds });
        }}
      >
        <span className="r42-eyebrow">Users & access</span>
        <h2 id="invite-user-title">Invite a user</h2>
        <p>Invite someone to this organization and choose their starting access level. Route42 will create a tenant-specific membership without changing access they may have elsewhere.</p>

        <label className="r42-field">
          <span className="r42-field__label">Email address <b>Required</b></span>
          <input autoFocus type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="technician@example.com" disabled={busy} />
        </label>

        <label className="r42-field">
          <span className="r42-field__label">Role <b>Required</b></span>
          <select value={roleId} onChange={(event) => setRoleId(event.target.value as RoleId)} disabled={busy}>
            {roleCatalog.map((role) => <option key={role.id} value={role.id}>{role.label}</option>)}
          </select>
          <small>{roleCatalog.find((role) => role.id === roleId)?.description}</small>
        </label>

        <div className="r42-facility-scope-picker" aria-label="Facility scope">
          <span className="r42-field__label">Facility scope</span>
          <div className="r42-facility-scope-options">
            {allowOrganizationWide ? <label className="r42-facility-scope-option">
              <input type="radio" name="facility-scope" checked={scopeMode === 'ALL'} onChange={() => setScopeMode('ALL')} disabled={busy} />
              <span><strong>All facilities</strong><small>Organization-wide access, including facilities added later.</small></span>
            </label> : null}
            {facilities.length ? <label className="r42-facility-scope-option">
              <input type="radio" name="facility-scope" checked={scopeMode === 'SELECTED'} onChange={() => setScopeMode('SELECTED')} disabled={busy} />
              <span><strong>Selected facilities</strong><small>Limit this membership to specific facilities.</small></span>
            </label> : null}
          </div>
          {scopeMode === 'SELECTED' ? <div className="r42-role-options" aria-label="Select facilities">
            {facilities.map((facility) => <label className={`r42-role-option${selectedFacilityIds.includes(facility.facility_id) ? ' selected' : ''}`} key={facility.facility_id}>
              <input type="checkbox" checked={selectedFacilityIds.includes(facility.facility_id)} onChange={() => toggleFacility(facility.facility_id)} disabled={busy} />
              <span><strong>{facility.facility_name}</strong><small>{facility.location ?? 'Location not specified'}</small></span>
            </label>)}
          </div> : null}
          {!allowOrganizationWide ? <small>Your membership is facility-scoped. Invitations must remain within the facilities you can access.</small> : null}
          {!facilities.length && allowOrganizationWide ? <small>No facilities are configured yet. This invitation will be organization-wide.</small> : null}
          {!facilities.length && !allowOrganizationWide ? <small>No accessible facilities are available for delegation.</small> : null}
        </div>

        {error ? <div className="r42-form-alert" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><div><strong>Invitation could not be completed.</strong><span>{error}</span></div></div> : null}

        <div className="r42-dialog-actions">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn r42-btn-primary" disabled={!valid || busy}>
            {busy ? <><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Inviting…</> : 'Send invitation'}
          </button>
        </div>
      </form>
    </div>
  );
}
